
import { Component, Prop, Mixins, Watch } from 'vue-property-decorator';
import MqttMixIn from '../mixins/mqttManagement';
// Using global Vuetify registration
import axios from 'axios';

export interface MqttTopicStatus {
  name: string;
  topic: string;
  connected: boolean;
  timerId?: ReturnType<typeof setTimeout>;
}

export interface RestStatus {
  name: string;
  url: string;
  connected: boolean;
  timerId?: ReturnType<typeof setTimeout>;
}

/**
 * Subscribes to and monitors connected status of Flightpath and ROS REST and MQTT endpoints
 */
type Status = { name: string; connected: boolean };

@Component({})
export default class extends Mixins(MqttMixIn) {
  @Prop({ default: false }) reset!: boolean;
  @Prop({ required: true }) PubSub: any;
  @Prop({ required: true }) config!: {
    flightpathRest: string;
    rosRest: string;
    flightpathMqtt: string;
    rosMqtt: string;
  };

  mqttStatuses: MqttTopicStatus[] = [];
  restStatuses: RestStatus[] = [];

  get statuses(): Status[] {
    return [...this.mqttStatuses, ...this.restStatuses].map((s) => ({ name: s.name, connected: s.connected }));
  }

  heartBeatTimeout = 2000; // Twice the expected message rate

  mounted() {
    this.initializeAllTopics();
    this.subscribeToTopics();
    this.pollRestEndpoints();
  }

  destroyed() {
    this.clearTimerIds();
    this.removeMqttVisibility();
  }

  @Watch('reset')
  onReset() {
    this.clearTimerIds();
    this.resetConnection();
  }

  /**
   * Initialize all topics to false, so they show up in the subscription list
   */
  initializeAllTopics() {
    this.restStatuses.push({ name: 'FlightPath REST', url: this.config.flightpathRest, connected: false });
    this.restStatuses.push({ name: 'ROS REST', url: this.config.rosRest, connected: false });
    this.mqttStatuses.push({ name: 'FlightPath MQTT', topic: this.config.flightpathMqtt, connected: false });
    this.mqttStatuses.push({ name: 'ROS MQTT', topic: this.config.rosMqtt, connected: false });
  }

  /**
   * Subscribe to all topics.
   */
  async subscribeToTopics() {
    this.mqttStatuses.forEach((status) => {
      this.topicSubscription(status.topic, status.timerId);
    });
    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
  }

  /**
   * Subscribe to and monitor topic subscription statuses.
   * Clear and restart the Timeout (which sets subscription status to false) if another message is
   * received within the interval of heartBeatTimeout.
   * @param topic
   * @param timerId
   */
  topicSubscription(topic: string, timerId?: ReturnType<typeof setTimeout>) {
    this.addMqttVisibility(topic, () =>
      this.PubSub.subscribe(topic).subscribe({
        next: () => {
          if (timerId) clearInterval(timerId);
          this.updateTopic(topic, true);
          timerId = setTimeout(() => this.updateTopic(topic, false), this.heartBeatTimeout);
        },
        error: (error: any) => console.log(error)
      })
    );
  }

  /**
   * Update connected status of topic.
   * @param topic
   * @param connected
   */
  updateTopic(topic: string, connected: boolean) {
    const index = this.mqttStatuses.findIndex((status) => status.topic == topic);
    if (index >= 0) {
      this.mqttStatuses[index].connected = connected;
    }
  }

  /**
   * Clear the status timers.
   */
  clearTimerIds() {
    this.mqttStatuses.forEach((status) => {
      if (status.timerId) clearInterval(status.timerId);
    });
    this.restStatuses.forEach((status) => {
      if (status.timerId) clearInterval(status.timerId);
    });
  }

  /**
   * Poll all REST endpoints for their status
   */
  pollRestEndpoints() {
    this.restStatuses.forEach(async (status) => {
      if (status.timerId) clearInterval(status.timerId);
      status.timerId = setInterval(async () => {
        await axios
          .get(status.url)
          .then((response) => {
            if (response.status === 200) {
              this.updateEndpoint(status.url, true);
            } else {
              this.updateEndpoint(status.url, false);
            }
          })
          .catch(() => {
            this.updateEndpoint(status.url, false);
          });
      }, this.heartBeatTimeout);
    });
  }

  /**
   * Update connected status of endpoint
   * @param url
   * @param connected
   */
  updateEndpoint(url: string, connected: boolean) {
    const index = this.restStatuses.findIndex((status) => status.url == url);
    if (index >= 0) {
      this.restStatuses[index].connected = connected;
    }
  }
}
